import { useCallback, useEffect, useRef, useState } from "react";
import {
    ExportCompletedContent,
    ExportPaymentCompletedContent,
    MercureMessage,
    RpcCallbackContent,
    TriggerKpiProgressContent,
    TriggerPromoCompletedContent,
    TriggerPromoProcessContent,
    WorkStatus,
} from "tm-shared/types";

import { MERCURE_HUB_URL } from "@/config";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { addModalToQueue, removeModalFromQueue } from "@/store/slices/ui";
import { decreaseExportIndicator } from "@/store/slices/work";
import { ModalType } from "@/types/modals";

export const useMercure = () => {
    const userId = useAppSelector((s) => s.user.id);
    const workStatus = useAppSelector((s) => s.work.workStatus);
    const dispatch = useAppDispatch();

    const [updateAvailable, setUpdateAvailable] = useState(false);
    const eventSourceRef = useRef<EventSource | null>(null);

    const connect = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const url = new URL(MERCURE_HUB_URL);
        url.searchParams.append("topic", userId);

        const eventSource = new EventSource(url.toString());
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
            try {
                const parsed: MercureMessage = JSON.parse(event.data);

                const { deliveryId = event.timeStamp.toString(), subject } =
                    parsed;

                console.log("Mercure data", parsed);

                switch (subject) {
                    case "trigger.promo.completed": {
                        const { consumedId }: TriggerPromoCompletedContent =
                            JSON.parse(parsed.content);

                        dispatch(removeModalFromQueue(consumedId));
                        break;
                    }
                    case "trigger.promo.process": {
                        const data: TriggerPromoProcessContent = JSON.parse(
                            parsed.content,
                        );

                        dispatch(
                            addModalToQueue({
                                id: data.consumedId,
                                type: ModalType.Promos,
                                payload: {
                                    deliveryId,
                                    promoInfo: {
                                        ...data,
                                    },
                                },
                            }),
                        );

                        // if (callbackObject?.content?.consumedId) {
                        //     dispatch(
                        //         addModalToQueue({
                        //             type: ModalType.Promos,
                        //             id: callbackObject?.content?.consumedId,
                        //             payload: {
                        //                 deliveryId,
                        //                 promoInfo: {
                        //                     ...callbackObject?.content,
                        //                 },
                        //             },
                        //         }),
                        //     );
                        // } else {
                        // }

                        break;
                    }
                    case "trigger.getState": {
                        dispatch(
                            addModalToQueue({
                                id: deliveryId,
                                type: ModalType.State,
                                payload: {
                                    ...parsed,
                                },
                            }),
                        );
                        break;
                    }
                    case "exportPayment.completed": {
                        const parsedContent: ExportPaymentCompletedContent =
                            JSON.parse(parsed.content);

                        dispatch(
                            addModalToQueue({
                                id: deliveryId,
                                type: ModalType.ExportPayment,
                                payload: {
                                    deliveryId,
                                    ...parsedContent,
                                },
                            }),
                        );

                        break;
                    }

                    case "export.completed": {
                        const callbackObject: ExportCompletedContent =
                            JSON.parse(parsed.content);

                        dispatch(
                            addModalToQueue({
                                id: deliveryId,
                                type: ModalType.Exports,
                                payload: {
                                    deliveryId,
                                    ...callbackObject,
                                },
                            }),
                        );
                        dispatch(decreaseExportIndicator());
                        break;
                    }
                    case "trigger.kpi.progress": {
                        const callbackObject: TriggerKpiProgressContent =
                            JSON.parse(parsed.content);

                        dispatch(
                            addModalToQueue({
                                id: deliveryId,
                                type: ModalType.KpiProgress,
                                payload: {
                                    deliveryId,
                                    ...callbackObject,
                                },
                            }),
                        );
                        break;
                    }
                    case "rpc.callback": {
                        const callbackObject: RpcCallbackContent = JSON.parse(
                            parsed.content,
                        );

                        console.log(callbackObject);

                        switch (callbackObject?.method) {
                            case "scheduler.addUnscheduledPointVisits": {
                                dispatch(
                                    addModalToQueue({
                                        id: deliveryId,
                                        type: ModalType.UnscheduledPointCompletion,
                                        payload: {
                                            deliveryId,
                                            pointsCount:
                                                callbackObject?.response?.result
                                                    ?.countUnscheduledPoints,
                                        },
                                    }),
                                );
                                break;
                            }

                            default: {
                                dispatch(
                                    addModalToQueue({
                                        id: deliveryId,
                                        type: ModalType.Messages,
                                        payload: {
                                            ...parsed,
                                        },
                                    }),
                                );
                                break;
                            }
                        }
                        break;
                    }
                    default: {
                        dispatch(
                            addModalToQueue({
                                id: deliveryId,
                                type: ModalType.Messages,
                                payload: {
                                    ...parsed,
                                },
                            }),
                        );
                        break;
                    }
                }
            } catch (error) {
                console.error("Помилка парсинга повідомлення:", error);
            }
        };

        eventSource.onerror = () => {
            console.error("Помилка підключення до Mercure. З'єднання закрито.");
            eventSource.close();
        };
    }, [dispatch, userId]);

    useEffect(() => {
        let swHandler: () => void;
        if ("serviceWorker" in navigator) {
            swHandler = () => setUpdateAvailable(true);
            navigator.serviceWorker.addEventListener(
                "controllerchange",
                swHandler,
            );
        }
        if (userId) {
            connect();
        }

        const handleReconnect = () => {
            if (document.visibilityState !== "visible") return;

            if (userId && workStatus === WorkStatus.StartWork) {
                console.log(
                    "Застосунок активовано! Перепідключаємо Mercure...",
                );
                connect();
            }
        };

        document.addEventListener("visibilitychange", handleReconnect);

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
            if ("serviceWorker" in navigator && swHandler) {
                navigator.serviceWorker.removeEventListener(
                    "controllerchange",
                    swHandler,
                );
            }
            document.removeEventListener("visibilitychange", handleReconnect);
        };
    }, [userId, workStatus, dispatch, connect]);

    return updateAvailable;
};
